/**
 * Generator-discrepancies dashboard defaults, shared between the read path
 * (encamp-waste-data-access, which enforces them in SQL) and the Oak dashboard
 * (which interpolates them into the on-page explainer and stat-card links).
 * Single source of truth so tuning a rule here can never desync the UI copy
 * from the behavior it describes.
 */

/**
 * A downward trend is only meaningful once it has persisted: Encamp doesn't
 * lower a facility's generator status unless a customer confirms, so
 * short-lived dips are noise. DOWNWARD rows below this many consecutive
 * sustained months are hidden by default; the gate lifts when a caller
 * explicitly filters by direction/type or sustained months.
 */
export const DOWNWARD_MIN_SUSTAINED_MONTHS = 12;

/**
 * Compliance-risk thresholds for UPWARD exceedances: risk escalates with how
 * many months the status run has spanned (first detected month through the
 * latest re-trigger, inclusive — gap months count, since the facility stayed
 * unaddressed the whole time). Deliberately simple v1 constants — tune as
 * Brandy's team reacts.
 */
export const RISK_ELEVATED_MIN_RUN_MONTHS = 3;
export const RISK_HIGH_MIN_RUN_MONTHS = 6;

/**
 * Fine-grained lifecycle stage of a generator-status discrepancy row — the
 * queue position derived from its resolution status plus the TPM validation
 * gate. One vocabulary shared by the read path's `stage:` filter/sort SQL, the
 * dashboard's Status dropdown and chips, and the staff CSV export, so a stage
 * can't be renamed in one surface and not the others.
 */
export const EXCEEDANCE_STAGE_IDS = [
  "AWAITING_VALIDATION",
  "READY_FOR_PICKUP",
  "IN_FLIGHT",
  "RESOLVED",
  "IGNORED",
] as const;

export type ExceedanceStage = (typeof EXCEEDANCE_STAGE_IDS)[number];

/** Human-readable stage labels — the exact chip text the dashboard renders. */
export const EXCEEDANCE_STAGE_LABELS: Record<ExceedanceStage, string> = {
  AWAITING_VALIDATION: "Needs Validation",
  READY_FOR_PICKUP: "Validated · Ready for Pickup",
  IN_FLIGHT: "In Flight",
  RESOLVED: "Resolved",
  IGNORED: "Ignored",
};

/**
 * Display labels for a discrepancy's direction (the dashboard's Type column and
 * the export's Type field). Keys match GeneratorStatusExceedanceDirection.
 */
export const EXCEEDANCE_DIRECTION_LABELS: Record<"UPWARD" | "DOWNWARD", string> = {
  UPWARD: "Exceedance",
  DOWNWARD: "Downward trend",
};

/** Display labels for the derived compliance-risk signal. */
export const EXCEEDANCE_RISK_LABELS: Record<"ELEVATED" | "HIGH", string> = {
  ELEVATED: "Elevated",
  HIGH: "High",
};
